import type { ApiKeyActivityResource, ApiKeyResource } from './types';

/**
 * Wat een sleutel mag. De waarden zijn die van de API (`ApiKeyScope`); de woorden
 * erbij staan in de copy van de feature. Op volgorde van het formulier: per
 * onderwerp eerst lezen, dan schrijven.
 */
export const API_KEY_SCOPES = [
    'orders:read',
    'orders:write',
    'planning:read',
    'planning:write',
    'clients:read',
    'clients:write',
    'reports:read',
    'reports:send',
    'invoices:read',
    'credits:read',
] as const;

export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

/** Wat een nieuwe sleutel aangevinkt krijgt: alleen lezen, niets wat iets verstuurt. */
export const DEFAULT_API_KEY_SCOPES: readonly ApiKeyScope[] = [
    'orders:read',
    'planning:read',
    'clients:read',
    'reports:read',
    'invoices:read',
    'credits:read',
];

export type ApiKeyState = ApiKeyResource['state'];

export interface ApiKeyPerson {
    uuid: string;
    name: string;
}

export interface ApiKey {
    uuid: string;
    name: string;
    scopes: string[];
    scopeLabels: string[];
    /** `sk_live_…abcd`: hoe de sleutel in de lijst te herkennen is. */
    masked: string;
    state: ApiKeyState;
    createdBy: ApiKeyPerson;
    createdAt: string | null;
    expiresAt: string | null;
    lastUsedAt: string | null;
    revokedAt: string | null;
    revokedBy: ApiKeyPerson | null;
}

export interface IntegrationAddresses {
    mcpUrl: string;
    openApiUrl: string;
    docsUrl: string;
}

export interface ApiKeyList {
    keys: ApiKey[];
    addresses: IntegrationAddresses;
}

/** Een net gemaakte sleutel, met het geheim dat je één keer ziet. */
export interface IssuedApiKey {
    key: ApiKey;
    secret: string;
    addresses: IntegrationAddresses;
}

export interface NewApiKeyInput {
    name: string;
    scopes: readonly string[];
    /** `2026-12-31`, of `null` voor een sleutel zonder einddatum. */
    expiresOn: string | null;
}

export interface ApiKeyActivity {
    uuid: string | null;
    description: string;
    at: string | null;
}

export function toApiKey(resource: ApiKeyResource): ApiKey {
    return {
        uuid: resource.uuid,
        name: resource.name,
        scopes: resource.scopes,
        scopeLabels: resource.scope_labels,
        masked: `${resource.prefix}…${resource.token_hint}`,
        state: resource.state,
        createdBy: resource.created_by,
        createdAt: resource.created_at,
        expiresAt: resource.expires_at,
        lastUsedAt: resource.last_used_at,
        revokedAt: resource.revoked_at,
        revokedBy: resource.revoked_by,
    };
}

export function toAddresses(meta: {
    mcp_url: string;
    openapi_url: string;
    docs_url: string;
}): IntegrationAddresses {
    return { mcpUrl: meta.mcp_url, openApiUrl: meta.openapi_url, docsUrl: meta.docs_url };
}

export function toApiKeyActivity(resource: ApiKeyActivityResource): ApiKeyActivity {
    return { uuid: resource.uuid, description: resource.description, at: resource.created_at };
}
